import { useEffect, useMemo, useRef, useState } from 'react';
import { EditReservation, TimeSlot } from 'types/reservations';
import { Event } from 'types/shared/documents/restaurants/event';
import server from 'utils/server';
import { BookingWindowStatus } from 'types/shared/helper/bookingWindow';
import {
  getDefaultTicketSelection,
  normalizeTicketSelection,
} from 'utils/ticketSession';

type ErrorObject = { occassion?: string; guests?: string; date?: string };

const useAvailableTimes = (
  restaurantId = '',
  data: EditReservation,
  setdata: React.Dispatch<React.SetStateAction<EditReservation | null>>,
  uid = ''
) => {
  const {
    occassion = '',
    guests = 0,
    date = '',
    time,
    reservationId: id = '',
    reservationLength: resLength = null,
    walkin,
    new: isNew = false,
  } = data;

  const oldTimeSlot = data.timeSlot;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ErrorObject | null>(null);
  const [availableTimes, setavailableTimes] = useState<TimeSlot[] | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [message, setmessage] = useState('');
  const [bookingWindow, setBookingWindow] = useState<BookingWindowStatus | null>(null);
  const [waitinglist, setwaitinglist] = useState<null | {
    offerWaitinglist: boolean;
    slots?: string[];
  }>(null);

  // Opening an existing reservation fetches the times too. Its stored tables
  // must survive that fetch until date, guests or occassion change once.
  const openedRef = useRef<{ id: string; params: string | null } | null>(
    null
  );
  const requestRef = useRef(0);

  const getAvailableTimes = async (
    occassion: string,
    guests: number,
    date: string,
    resLength: null | number,
    isNew: boolean
  ) => {
    const request = ++requestRef.current;
    setBookingWindow(null);

    try {
      let errorObj: ErrorObject = {};

      if (!occassion) {
        errorObj.occassion = 'Anlass fehlt';
      }

      if (!guests) {
        errorObj.guests = 'Anzahl an Gästen fehlt';
      }

      if (!date) {
        errorObj.date = 'Datum fehlt';
      }

      setErrors(errorObj);

      if (Object.keys(errorObj).length) {
        setLoading(false);
        return null;
      }

      const params = `${date}|${guests}|${occassion}`;

      if (openedRef.current?.id !== id) openedRef.current = { id, params };
      else if (openedRef.current.params !== params)
        openedRef.current.params = null;

      const keepStoredTables = !isNew && openedRef.current.params === params;

      setLoading(true);

      let url = `/v03/reservations/${restaurantId}/times/${date}/${occassion}`;

      const { data } = await Promise.race([
        server.post(url, {
          guests,
          date,
          id,
          uid,
          resLength: !walkin ? resLength : null,
          type: 'WAITER',
        }),
      ]);

      // A slower answer for older params must not overwrite a newer one.
      if (request !== requestRef.current) return;

      setLoading(false);
      setavailableTimes(data.availableTimes);
      setBookingWindow(data.bookingWindow ?? null);
      setEvents(data.events ?? []);

      let waitinglist =
        isNew && !!data?.waitinglist?.offerWaitinglist
          ? data.waitinglist
          : null;

      setwaitinglist(waitinglist);

      const targetTime = oldTimeSlot?.time ?? time;

      let timeSlot =
        data.availableTimes.find((t: TimeSlot) => t.time === targetTime) || null;

      // console.log('TESTING4', timeSlot, oldTimeSlot, data);

      if (!timeSlot && oldTimeSlot && walkin) {
        timeSlot = {
          ...oldTimeSlot,
          combinations: [],
          blockedSlots: 0,
          totalSlots: 0,
          maxGuestsReached: false,
          reservationLength: resLength || oldTimeSlot.reservationLength || 4,
        };
      }

      if (timeSlot) {
        const noAvailability =
          timeSlot.blockedSlots >= timeSlot.totalSlots ||
          timeSlot.maxGuestsReached ||
          !timeSlot.combinations.length;
        let hasWaitinglist = !!noAvailability && !!waitinglist;

        if (hasWaitinglist) {
          timeSlot.waitinglist = hasWaitinglist;
        }
      }

      setdata((d) =>
        d !== null
          ? (() => {
              const event =
                timeSlot?.eventId && data.events?.length
                  ? data.events.find((e: Event) => e.id === timeSlot?.eventId) ?? null
                  : null;
              const hasBookedTicket = !!d.ticketId;

              return {
                ...d,
                timeSlot,
                capacityExpectation: data.capacityExpectation ?? null,
                event: event ?? (hasBookedTicket ? d.event ?? null : null),
                ticketMode: hasBookedTicket
                  ? d.ticketMode
                  : event
                  ? d.ticketMode ?? 'reservation_only'
                  : 'reservation_only',
                ticket: hasBookedTicket
                  ? event && d.ticket
                    ? normalizeTicketSelection(event, guests, d.ticket)
                    : d.ticket
                  : event
                  ? d.ticket
                    ? normalizeTicketSelection(event, guests, d.ticket)
                    : getDefaultTicketSelection(event, guests)
                  : undefined,
                tables:
                  walkin || d.keepTables
                    ? d.tables
                    : timeSlot?.combinations?.find(
                        (t: any) =>
                          JSON.stringify(t.tables.sort()) ===
                          JSON.stringify(d?.tables?.sort() ?? [])
                      )?.tables ??
                      (keepStoredTables && d.tables?.length
                        ? d.tables
                        : timeSlot?.combinations?.[0]?.tables) ??
                      d.tables,
              };
            })()
          : null
      );

      if (data.availableTimes.length) {
      }

      if (data.message && typeof data.message === 'object') {
        setmessage(data.message.de);
      } else {
        setmessage('');
      }
    } catch (error: any) {
      if (request !== requestRef.current) return;

      setLoading(false);
      setEvents([]);
      setBookingWindow(null);

      if (error.response) {
        setmessage(
          error?.response?.data?.message ?? 'Es ist ein Fehler aufgetreten'
        );
      } else {
        setmessage('Problem mit Internetverbindung');
      }

      setavailableTimes([]);
    }
  };

  console.log({ data });

  const loadWalkinReservationLength = async () => {
    // The backend route GET /:restaurantId/walkin/:occassion requires a
    // non-empty :occassion segment. Skip the request when occassion (or
    // restaurantId) is missing, otherwise the URL collapses to
    // `/walkin/` and the backend returns 404.
    if (!restaurantId || !occassion) return;

    try {
      const { data } = await server.get(
        `/v03/reservations/${restaurantId}/walkin/${occassion}?guests=${guests}`
      );

      setdata((d) =>
        !!d
          ? {
              ...d,
              reservationLength: data.resLength || 4,
            }
          : null
      );
    } catch (error) {
      console.log(error);
    }
  };

  const reservationLength = useMemo(() => {
    console.log({
      resLength,
      tS: data.timeSlot,
      x: availableTimes?.[0]?.reservationLength ?? null,
    });

    if (resLength) return resLength;

    if (data.timeSlot) {
      return data.timeSlot.reservationLength;
    }

    if (!availableTimes) return null;

    return availableTimes?.[0]?.reservationLength ?? null;
  }, [availableTimes, resLength, data.timeSlot]);

  useEffect(() => {
    if (walkin && !resLength) loadWalkinReservationLength();
    getAvailableTimes(occassion, guests, date, resLength, isNew);
  }, [restaurantId, occassion, guests, date, id, uid, resLength, walkin, data.availabilityRevision]);

  console.log({ walkin, resLength });

  return {
    loading,
    errors,
    setErrors,
    availableTimes,
    events,
    reservationLength,
    message,
    waitinglist,
    bookingWindow,
  };
};

export default useAvailableTimes;
